// src/components/analytics/KpiDashboardPage.tsx
// CM-1f — KPI dashboard leyendo los endpoints nuevos de analytics
// (/admin/analytics/{summary,trends,campaigns,roi,sources}).
//
// Las métricas aquí son Meta Ads-centric. Overview (F1) sigue siendo
// la vista cross-channel. Este tab es para drilling down en performance
// de Meta campaigns con los KPIs jaleapp-parity (CTR/CPC/CPM/ROAS/CVR).

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle, DollarSign, Eye, Loader2, MousePointerClick,
  RefreshCw, Target, TrendingUp,
} from 'lucide-react';

import {
  ApiError,
  centsToCurrency, formatInt, formatPercent, formatRatio,
  getCampaignCards, getRoi, getSourceBreakdown, getSummary, getTrends,
  type CampaignKpi, type KpiSummary, type KpiTrendPoint, type RoiSummary,
  type SourceBreakdown,
} from '@/lib/api_analytics';

type Notifier = (n: { type: 'success' | 'error' | 'info'; title: string; message?: string }) => void;
interface Props { addNotification?: Notifier }

const WINDOWS = [
  { label: '7 días', days: 7 },
  { label: '30 días', days: 30 },
  { label: '90 días', days: 90 },
];

export function KpiDashboardPage({ addNotification }: Props) {
  const [days, setDays] = useState(30);
  const [summary, setSummary] = useState<KpiSummary | null>(null);
  const [trends, setTrends] = useState<KpiTrendPoint[]>([]);
  const [campaigns, setCampaigns] = useState<CampaignKpi[]>([]);
  const [roi, setRoi] = useState<RoiSummary | null>(null);
  const [sources, setSources] = useState<SourceBreakdown | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const [s, t, c, r, b] = await Promise.all([
        getSummary(days),
        getTrends(days),
        getCampaignCards(days, 20),
        getRoi(days),
        getSourceBreakdown(days),
      ]);
      setSummary(s);
      setTrends(t);
      setCampaigns(c);
      setRoi(r);
      setSources(b);
    } catch (e: any) {
      const msg = e instanceof ApiError ? e.message : e?.message || 'Error';
      setErr(msg);
      addNotification?.({ type: 'error', title: 'No cargaron KPIs', message: msg });
    } finally {
      setLoading(false);
    }
  }, [days, addNotification]);

  useEffect(() => { load(); }, [load]);

  const kpis = useMemo(() => [
    { label: 'Gasto', value: centsToCurrency(summary?.spend_cents ?? null), icon: <DollarSign size={16} /> },
    { label: 'Impresiones', value: formatInt(summary?.impressions ?? null), icon: <Eye size={16} /> },
    { label: 'Clicks', value: formatInt(summary?.clicks ?? null), icon: <MousePointerClick size={16} /> },
    { label: 'CTR', value: formatPercent(summary?.ctr ?? null), icon: <TrendingUp size={16} /> },
    { label: 'CPC', value: centsToCurrency(summary?.cpc_cents ?? null), icon: <DollarSign size={16} /> },
    { label: 'CPM', value: centsToCurrency(summary?.cpm_cents ?? null), icon: <DollarSign size={16} /> },
    { label: 'Conversiones', value: formatInt(Math.round(summary?.conversions ?? 0)), icon: <Target size={16} /> },
    { label: 'ROAS', value: formatRatio(summary?.roas ?? null), icon: <TrendingUp size={16} /> },
  ], [summary]);

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-white">KPIs de Meta Ads</h1>
          <p className="text-xs text-white/50">
            {summary
              ? `Ventana ${summary.window_start} → ${summary.window_end}`
              : 'Cargando ventana…'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {WINDOWS.map(w => (
            <button
              key={w.days}
              onClick={() => setDays(w.days)}
              className={`px-3 py-1.5 rounded-lg text-xs border ${
                days === w.days
                  ? 'bg-[var(--acc1)]/20 border-[var(--acc1)]/40 text-[var(--acc1)]'
                  : 'bg-white/5 border-white/10 text-white/70 hover:bg-white/10'
              }`}
            >{w.label}</button>
          ))}
          <button
            onClick={load}
            disabled={loading}
            className="p-2 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-white/70"
            title="Refrescar"
          >
            {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
          </button>
        </div>
      </div>

      {err && (
        <div className="o-card bg-red-500/10 border-red-500/30 text-red-200 text-sm flex items-center gap-2">
          <AlertTriangle size={14} /> {err}
        </div>
      )}

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {kpis.map(k => (
          <div key={k.label} className="o-card">
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-wide text-white/50 mb-1">
              {k.icon} {k.label}
            </div>
            <div className="text-xl font-semibold text-white tabular-nums">{k.value}</div>
          </div>
        ))}
      </div>

      {/* Trends chart (SVG inline — sin dep externa de charts) */}
      <div className="o-card">
        <h2 className="text-sm font-semibold text-white/80 mb-3">Tendencia diaria</h2>
        <TrendsChart points={trends} />
      </div>

      {/* Source breakdown + ROI */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="o-card">
          <h2 className="text-sm font-semibold text-white/80 mb-3">Por fuente</h2>
          <SourcesTable sources={sources} />
        </div>
        <div className="o-card">
          <h2 className="text-sm font-semibold text-white/80 mb-3">ROI</h2>
          <RoiWidget roi={roi} />
        </div>
      </div>

      {/* Campaign cards */}
      <div className="o-card">
        <h2 className="text-sm font-semibold text-white/80 mb-3">Por campaña</h2>
        <CampaignCardsGrid rows={campaigns} />
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// Trend chart (SVG puro — sin recharts/chartjs)
// ═══════════════════════════════════════════════════════════════

function TrendsChart({ points }: { points: KpiTrendPoint[] }) {
  if (!points.length) return <div className="text-sm text-white/50">Sin datos en la ventana.</div>;
  const width = 800;
  const height = 180;
  const pad = 28;

  const maxSpend = Math.max(1, ...points.map(p => p.spend_cents));
  const maxConv = Math.max(1, ...points.map(p => p.conversions));

  const xStep = (width - pad * 2) / Math.max(1, points.length - 1);
  const yFor = (val: number, max: number) =>
    height - pad - ((val / max) * (height - pad * 2));

  const spendPath = points.map((p, i) =>
    `${i === 0 ? 'M' : 'L'} ${pad + i * xStep} ${yFor(p.spend_cents, maxSpend)}`
  ).join(' ');
  const convPath = points.map((p, i) =>
    `${i === 0 ? 'M' : 'L'} ${pad + i * xStep} ${yFor(p.conversions, maxConv)}`
  ).join(' ');

  return (
    <div className="w-full overflow-x-auto">
      <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-48">
        <rect x="0" y="0" width={width} height={height} fill="transparent" />
        {/* Axes */}
        <line x1={pad} y1={height - pad} x2={width - pad} y2={height - pad} stroke="rgba(255,255,255,0.1)" />
        <line x1={pad} y1={pad} x2={pad} y2={height - pad} stroke="rgba(255,255,255,0.1)" />
        {/* Spend line */}
        <path d={spendPath} fill="none" stroke="var(--acc1, #00e5ff)" strokeWidth="2" />
        {/* Conversions line */}
        <path d={convPath} fill="none" stroke="#22c55e" strokeWidth="2" strokeDasharray="4 3" />
        {/* Labels */}
        <text x={pad} y={14} fill="rgba(255,255,255,0.65)" fontSize="10">
          Spend / día (max {centsToCurrency(maxSpend)}) — conv. max {Math.round(maxConv)}
        </text>
      </svg>
      <div className="flex items-center gap-4 text-[11px] text-white/60">
        <span className="flex items-center gap-1">
          <span className="inline-block w-3 h-0.5 bg-[var(--acc1,#00e5ff)]" /> Gasto
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-block w-3 h-0.5 bg-green-500" style={{borderTop: '1px dashed'}} /> Conversiones
        </span>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// Sources table
// ═══════════════════════════════════════════════════════════════

function SourcesTable({ sources }: { sources: SourceBreakdown | null }) {
  if (!sources) return <div className="text-sm text-white/50">Sin datos.</div>;
  const entries = Object.entries(sources.sources);
  if (!entries.length) return <div className="text-sm text-white/50">No hay fuentes con actividad.</div>;
  return (
    <table className="w-full text-sm">
      <thead className="text-[11px] uppercase text-white/50">
        <tr>
          <th className="text-left py-1">Fuente</th>
          <th className="text-right py-1">Gasto</th>
          <th className="text-right py-1">CTR</th>
          <th className="text-right py-1">ROAS</th>
        </tr>
      </thead>
      <tbody>
        {entries.map(([name, k]) => (
          <tr key={name} className="border-t border-white/5">
            <td className="py-1.5 text-white/80">{name}</td>
            <td className="text-right text-white/80 tabular-nums">{centsToCurrency(k.spend_cents)}</td>
            <td className="text-right text-white/80 tabular-nums">{formatPercent(k.ctr)}</td>
            <td className="text-right text-white/80 tabular-nums">{formatRatio(k.roas)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ═══════════════════════════════════════════════════════════════
// ROI widget
// ═══════════════════════════════════════════════════════════════

function RoiWidget({ roi }: { roi: RoiSummary | null }) {
  if (!roi) return <div className="text-sm text-white/50">Sin datos.</div>;
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-2 text-sm">
        <div>
          <div className="text-[11px] uppercase text-white/50">Gasto</div>
          <div className="text-white tabular-nums">{centsToCurrency(roi.total_spend_cents)}</div>
        </div>
        <div>
          <div className="text-[11px] uppercase text-white/50">Revenue</div>
          <div className="text-white tabular-nums">{centsToCurrency(roi.total_revenue_cents)}</div>
        </div>
        <div>
          <div className="text-[11px] uppercase text-white/50">ROAS</div>
          <div className={`tabular-nums ${
            roi.roas == null ? 'text-white/50' : roi.roas >= 1 ? 'text-green-400' : 'text-red-400'
          }`}>{formatRatio(roi.roas)}</div>
        </div>
        <div>
          <div className="text-[11px] uppercase text-white/50">Costo / conversión</div>
          <div className="text-white tabular-nums">{centsToCurrency(roi.cost_per_conversion_cents)}</div>
        </div>
      </div>
      {!!roi.by_campaign.length && (
        <div className="text-[11px] text-white/50 pt-1 border-t border-white/5">
          Top {Math.min(5, roi.by_campaign.length)} campañas por ROAS:
          <ul className="mt-1 space-y-0.5">
            {roi.by_campaign
              .filter(c => c.roas != null)
              .sort((a, b) => (b.roas ?? 0) - (a.roas ?? 0))
              .slice(0, 5)
              .map(c => (
                <li key={c.campaign_id} className="flex justify-between text-white/75">
                  <span className="truncate pr-2">{c.campaign_name}</span>
                  <span className="tabular-nums">{formatRatio(c.roas)}</span>
                </li>
              ))}
          </ul>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// Campaign cards grid
// ═══════════════════════════════════════════════════════════════

function CampaignCardsGrid({ rows }: { rows: CampaignKpi[] }) {
  if (!rows.length) return <div className="text-sm text-white/50">Sin campañas con gasto en la ventana.</div>;
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
      {rows.map(c => (
        <div key={c.campaign_id} className="rounded-lg bg-white/5 border border-white/10 p-3">
          <div className="flex items-start justify-between mb-2">
            <div className="min-w-0">
              <div className="text-sm font-semibold text-white truncate">{c.campaign_name}</div>
              <div className="text-[10px] uppercase text-white/40">
                {c.status} · {c.objective || '—'}
              </div>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-x-2 gap-y-1 text-xs">
            <KV label="Gasto" v={centsToCurrency(c.spend_cents)} />
            <KV label="Impr." v={formatInt(c.impressions)} />
            <KV label="Clicks" v={formatInt(c.clicks)} />
            <KV label="CTR" v={formatPercent(c.ctr)} />
            <KV label="CPC" v={centsToCurrency(c.cpc_cents)} />
            <KV label="ROAS" v={formatRatio(c.roas)} />
          </div>
        </div>
      ))}
    </div>
  );
}

function KV({ label, v }: { label: string; v: string }) {
  return (
    <div className="flex flex-col">
      <span className="text-[10px] text-white/40 uppercase">{label}</span>
      <span className="text-white/90 tabular-nums">{v}</span>
    </div>
  );
}

export default KpiDashboardPage;
